import {
  Box,
  Button,
  FormControlLabel,
  styled,
  Switch,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SpecialRisksGrid from "./SpecialRisksGrid";
import ViewAllSpecialRisksDialog from "./ViewAllSpecialRisksDialog";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gaps,
  margin,
  padding,
} from "theme/defaultStyle";
import NoSpecialRisksDialog from "./NoSpecialRisksDialog";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import { useLookup } from "utils/context/LookupContext";
import { SelectFieldOption } from "controls/global/select-field/SelectInput";

interface SpecialRisksProps {
  schema: string;
  disabled?: boolean;
  existingSpecialRisks: SelectFieldOption[];
  revising: boolean;
}

export type SelectedSpecialRiskType = {
  id: string;
  configCode?: string;
  integrationKey?: string;
  selectedSpecialRisk?: string;
  uniqueIdentifier?: string;
};

const interFont = "Inter, sans-serif !important";

const StyledSpecialRisksContainer = styled(Box)({
  padding: padding.xlarge1,
  border: `1px solid ${colors.grey09}`,
  borderRadius: borderRadius.small,
  fontFamily: interFont,
});

const StyledActionsBox = styled(Box)({
  display: `flex`,
  gap: gaps.large1,
  alignItems: `center`,
});

const StyledHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: margin.xlarge1,
  gap: `${gaps.small1}`,
});

const SpecialRisksTitle = styled(Typography)({
  color: colors.blue01,
  fontSize: fontSize.xlarge,
  fontStyle: `normal`,
  fontWeight: fontWeight.bold1,
  lineHeight: `normal`,
  fontFamily: interFont,
});

const SpecialRisksError = styled(Box)({
  color: colors.red01,
  fontSize: fontSize.medium,
  fontWeight: fontWeight.normal1,
  lineHeight: `normal`,
});

const StyledButton = styled(Button)({
  fontWeight: `${fontWeight.bold1} !important`,
  fontFamily: interFont,
  "&.Mui-disabled": {
    background: "none",
    color: colors.blue12,
  }
});

const StyledFormControlLabel = styled(FormControlLabel)({
  margin: margin.zero,
  "& .MuiFormControlLabel-label": {
    fontFamily: interFont,
  },
});

export const getBlankRow = () => ({
  id: uuidv4(),
  configCode: undefined,
  integrationKey: undefined,
  selectedSpecialRisk: undefined,
  uniqueIdentifier: undefined,
});

export default function SpecialRisks({
  schema,
  disabled,
  existingSpecialRisks,
  revising,
}: SpecialRisksProps) {

  const {
    setValue,
    getValues,
    // trigger,
    formState: { errors },
  } = useFormWrapper();
  const name = `${schema}.uwaRequestInfo.specialRisks`;
  const [isNoSpecialRisksEnabled, setIsNoSpecialRisksEnabled] =
    useState<boolean>(
      // typeof getValues(`${schema}.uwaRequestInfo.isNoSpecialRisks`) === 'boolean' ? !(getValues(`${schema}.uwaRequestInfo.isNoSpecialRisks`)) : false
      getValues(`${schema}.uwaRequestInfo.isNoSpecialRisks`) || false
    );
  const [isViewAllSpecialRisksDialogOpen, setIsViewAllSpecialRisksDialogOpen] =
    useState(false);
  const [selectedSpecialRisks, setSelectedSpecialRisks] = useState<
    SelectedSpecialRiskType[]
  >([getBlankRow()]);
  const [isNoSpecialRisksDialogOpen, setIsNoSpecialRisksDialogOpen] =
    useState(false);
  const specialRisksRef = useRef<{ [key: string]: any }>({});

  const [specialRiskItems, setSpecialRiskItems] = useState<any[]>([]);
  const [, { getSpecialRiskItems }] = useLookup();

  const productIndex = schema.split(".")[1];

  const specialRisksError = (errors as any)?.uwaProducts?.[productIndex]
    ?.uwaRequestInfo?.specialRisks;
  const specialRiskErrorMessage = specialRisksError?.message;
  const hasSpecialRisksError = Boolean(specialRiskErrorMessage);

  const updateFormValues = (risks: SelectedSpecialRiskType[]) => {
    const formValues = risks.map((risk: SelectedSpecialRiskType) => ({
      text: risk.selectedSpecialRisk,
      value: risk.configCode,
    }));
    setValue(name,formValues, {shouldDirty: true, shouldValidate: true, shouldTouch: true});
    //trigger(name);
  };

  useEffect(() => {
    setValue(`${schema}.uwaRequestInfo.isNoSpecialRisks`, isNoSpecialRisksEnabled);
  }, [isNoSpecialRisksEnabled, setValue, schema]);

  const handleNoSpecialRisksToggle = () => {
    if (
      !isNoSpecialRisksEnabled &&
      selectedSpecialRisks.some((r) => !!r.selectedSpecialRisk)
    ) {
      setIsNoSpecialRisksDialogOpen(true);
      return;
    }

    setValue(name, [], {shouldDirty: true, shouldValidate: true, shouldTouch: true});
    setIsNoSpecialRisksEnabled((prevState) => !prevState);
  };

  useEffect(() => {
    const fetchSpecialRiskItems = async () => {
      const items = await getSpecialRiskItems();
      setSpecialRiskItems(items.apiResponse || []);

      const mapSpecialRiskItemsWithIntegrationKey: { [key: string]: any } = {};
      (items.apiResponse || []).forEach((item: any) => {
        mapSpecialRiskItemsWithIntegrationKey[item.ConfigCode] = item;
      });
      specialRisksRef.current = mapSpecialRiskItemsWithIntegrationKey;
    };
    fetchSpecialRiskItems();
  }, [getSpecialRiskItems]);

  const specialRiskOptions = useMemo(() => {
    const selectedValues = selectedSpecialRisks.map((risk) => risk.configCode);

    return specialRiskItems
      .filter((item) => !selectedValues.includes(item.ConfigCode))
      .map((item) => ({
        text: item.ConfigValue,
        value: item.ConfigCode,
        integrationKey: item.IntegrationKey,
        uniqueIdentifier: item.IntegrationKey,
      }));
  }, [specialRiskItems, selectedSpecialRisks]);

  const confirmNoSpecialRisks = () => {
    setSelectedSpecialRisks([getBlankRow()]);
    //setValue(`${schema}.uwaRequestInfo.specialRisks`, []);
    setValue(name, []);
    setIsNoSpecialRisksDialogOpen(false);
    setIsNoSpecialRisksEnabled(true);
  };

  useEffect(() => {
    // const formSelectedSpecialRisks: SelectFieldOption[] = getValues(
    //   `${schema}.uwaRequestInfo.specialRisks`
    // );

    if (
      // Array.isArray(formSelectedSpecialRisks) &&
      // formSelectedSpecialRisks.length > 0
      existingSpecialRisks.length > 0 && !isNoSpecialRisksEnabled
    ) {
      const mappedRisks = existingSpecialRisks.map((risk: SelectFieldOption) => {
        const specialRisk: SelectedSpecialRiskType = {
          id: uuidv4(),
          integrationKey: specialRisksRef.current[risk.value]?.IntegrationKey,
          selectedSpecialRisk: risk.text,
          configCode: risk.value,
          uniqueIdentifier: specialRisksRef.current[risk.value]?.IntegrationKey,
        };
        return specialRisk;
      });

      let updated = disabled
        ? mappedRisks
        : mappedRisks.filter(item => !(item.configCode === '' && item.selectedSpecialRisk === ''));

      // Ensuring one blank row exists when revising
      if (!disabled && updated.length > 0) {
        const last = updated[updated.length - 1];
        if (last.selectedSpecialRisk) {
          updated = [...updated, getBlankRow()];
        }
      }

      setSelectedSpecialRisks(updated);
    }
  }, [setValue, getValues, schema, existingSpecialRisks, revising]);

  useEffect(() => {
    if (disabled) return;

    setSelectedSpecialRisks(prev => {
      const last = prev[prev.length - 1];

      // If last row has a selected value then append blank row
      if (last && last.selectedSpecialRisk) {
        return [...prev, getBlankRow()];
      }

      // blank row already exists
      return prev; 
    });
  }, [selectedSpecialRisks, disabled]);

  useEffect(() => {
    if(!isNoSpecialRisksEnabled && revising && existingSpecialRisks?.length === 0) {
      setSelectedSpecialRisks((prev) => {
        if (!prev || prev.length === 0) {
          return [getBlankRow()];
        }
        return prev;
      });
    }
  }, [isNoSpecialRisksEnabled, revising, existingSpecialRisks?.length])

  return (
    <StyledSpecialRisksContainer>
      <StyledHeader>
        <SpecialRisksTitle>Special Risks</SpecialRisksTitle>
        {hasSpecialRisksError && (
          <>
            <ErrorOutlineIcon sx={{ color: colors.red01 }} fontSize="large" />
            <SpecialRisksError>{specialRiskErrorMessage}</SpecialRisksError>
          </>
        )}
      </StyledHeader>
      {!isNoSpecialRisksEnabled ? (
        <SpecialRisksGrid
          // schema={`${schema}.uwaRequestInfo.specialRisks`}
          schema={name}
          disabled={disabled}
          hasError={hasSpecialRisksError}
          specialRiskOptions={specialRiskOptions}
          selectedSpecialRisks={selectedSpecialRisks}
          setSelectedSpecialRisks={setSelectedSpecialRisks}
          updateFormValues={updateFormValues}
          specialRiskItems={specialRiskItems}
        />
      ) : null}

      {!disabled || isNoSpecialRisksEnabled ? (
        <StyledActionsBox>
          <StyledButton
            variant="text"
            disableRipple
            disableFocusRipple
            disabled={disabled}
            onClick={() => {
              setIsViewAllSpecialRisksDialogOpen(true);
            }}
          >
            View All Special Risks
          </StyledButton>
          <StyledFormControlLabel
            value="end"
            control={
              <Switch color="primary" checked={isNoSpecialRisksEnabled} />
            }
            onChange={handleNoSpecialRisksToggle}
            label="No Special Risks"
            labelPlacement="end"
            disabled={disabled}
          />
        </StyledActionsBox>
      ) : (
        <></>
      )}

      {isViewAllSpecialRisksDialogOpen && !disabled ? (
        <ViewAllSpecialRisksDialog
          isOpen={isViewAllSpecialRisksDialogOpen}
          specialRiskItems={specialRiskItems}
          isNoSpecialRisksEnabled={isNoSpecialRisksEnabled}
          selectedSpecialRisks={selectedSpecialRisks}
          setSelectedSpecialRisks={setSelectedSpecialRisks}
          updateFormValues={updateFormValues}
          onClose={() => setIsViewAllSpecialRisksDialogOpen(false)}
        />
      ) : null}

      <NoSpecialRisksDialog
        confirmationMessage="No Special Risks?"
        textBelowModal="By continuing, your previously selected special risks will be cleared."
        isOpen={isNoSpecialRisksDialogOpen}
        onNo={() => setIsNoSpecialRisksDialogOpen(false)}
        onYes={confirmNoSpecialRisks}
        singleActionButtonText="Continue"
        buttonNoText="Cancel"
      />
    </StyledSpecialRisksContainer>
  );
}
